// Iter 82.14 / Phase E3.2 Sprint 4 polish — Period Close Certificate print route.
//
// Renders the immutable certificate at A4 for Playwright PDF export.
// Mirrors PayslipPrint.jsx — minimal styling, A4 page-break, prominent
// SHA-256 hash + "Hash verified" indicator. The certificate fields are
// shown verbatim from the stored doc (D12 — operator-locked layout).
import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export default function PeriodCloseCertificatePrint() {
    const { closeId } = useParams();
    const [sp] = useSearchParams();
    const certVersion = sp.get("cert_version");
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const r = await api.get(`/payroll/period/close/${closeId}/certificates`);
                if (!active) return;
                setData(r.data);
            } catch (e) {
                if (!active) return;
                setErr(formatApiError(e, "Failed to load certificate"));
            }
        })();
        return () => { active = false; };
    }, [closeId]);

    const cert = useMemo(() => {
        if (!data?.certificates?.length) return null;
        if (certVersion) {
            const v = parseInt(certVersion, 10);
            return data.certificates.find((c) => c.cert_version === v) || data.certificates[data.certificates.length - 1];
        }
        return data.certificates[data.certificates.length - 1];
    }, [data, certVersion]);

    if (err) return <div className="p-6 text-rose-700">{err}</div>;
    if (!cert) return <div className="p-6 text-slate-400">Loading certificate…</div>;

    return (
        <div className="bg-white text-slate-900 print:p-0 p-10 font-serif" data-testid="certificate-cover" style={{ minHeight: "100vh" }}>
            <style>{`@page { size: A4; margin: 16mm 14mm 24mm 14mm; }
                     @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }`}</style>

            {/* Header */}
            <header className="border-b-2 border-slate-900 pb-4 mb-6">
                <div className="flex justify-between items-baseline">
                    <div>
                        <div className="text-xs uppercase tracking-wider text-slate-500">REEDOS · Billing & Commercial</div>
                        <h1 className="text-3xl font-bold mt-1">Period Close Certificate</h1>
                        <div className="text-xs text-slate-500 mt-1">D12 — Immutable Audit Artefact · v{cert.cert_version}</div>
                    </div>
                    <div className="text-right">
                        <div className="text-[10px] uppercase text-slate-500">Period</div>
                        <div className="text-2xl font-mono font-bold">{cert.period_month}</div>
                    </div>
                </div>
            </header>

            {/* Attestation paragraph */}
            <section className="mb-8 leading-relaxed text-sm">
                <p>
                    This certificate attests that the payroll for the period
                    <strong> {cert.period_month}</strong> has been formally closed at REEDOS
                    on <strong>{cert.closed_at?.slice(0, 19).replace("T", " ")} UTC</strong> by
                    <strong> {cert.closed_by_email}</strong>. The figures below are the
                    immutable totals captured at the moment of closure. Any subsequent reopen
                    or reclose will not mutate this certificate — a new version will be
                    appended instead. The SHA-256 hash at the bottom of this page is the
                    tamper-evident seal over this body.
                </p>
            </section>

            {/* Key financials */}
            <section className="mb-8">
                <h2 className="text-xs uppercase tracking-wider text-slate-500 mb-3 border-b pb-1">Key Financials</h2>
                <table className="w-full text-sm">
                    <tbody className="divide-y divide-slate-200">
                        <tr><td className="py-2 text-slate-600">Employee Count</td><td className="py-2 text-right font-mono">{cert.employee_count}</td></tr>
                        <tr><td className="py-2 text-slate-600">Number of Payroll Runs</td><td className="py-2 text-right font-mono">{cert.run_count}</td></tr>
                        <tr><td className="py-2 text-slate-600">Gross Payroll</td><td className="py-2 text-right font-mono">{inr(cert.gross_payroll)}</td></tr>
                        <tr><td className="py-2 text-slate-600">Net Payroll Paid</td><td className="py-2 text-right font-mono">{inr(cert.net_payroll)}</td></tr>
                        <tr><td className="py-2 text-slate-600">Employer Contributions</td><td className="py-2 text-right font-mono">{inr(cert.employer_contributions)}</td></tr>
                        <tr><td className="py-2 text-slate-600">Reopen Count</td><td className="py-2 text-right font-mono">{cert.reopened_count}</td></tr>
                    </tbody>
                </table>
            </section>

            {/* Signatures */}
            <section className="grid grid-cols-2 gap-12 mb-12 text-xs">
                <div>
                    <div className="border-b border-slate-400 h-12"></div>
                    <div className="mt-1 text-slate-500">Closed By</div>
                    <div className="font-mono">{cert.closed_by_email}</div>
                </div>
                <div>
                    <div className="border-b border-slate-400 h-12"></div>
                    <div className="mt-1 text-slate-500">Closed At (UTC)</div>
                    <div className="font-mono">{cert.closed_at?.slice(0, 19).replace("T", " ")}</div>
                </div>
            </section>

            {/* Tamper-evident seal */}
            <footer className="border-t-2 border-slate-900 pt-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Tamper-Evident Seal · SHA-256</div>
                <div className="font-mono text-[10px] break-all leading-tight" data-testid="cert-print-hash">
                    {cert.certificate_hash}
                </div>
                <div className="text-[10px] text-slate-400 mt-2">
                    Certificate version {cert.cert_version} · REEDOS Period Close Workflow · D12.
                </div>
            </footer>
        </div>
    );
}
